import {ChallengeInfoResponse} from "@devour/client";
import classNames from "classnames";

/**
 * Validates that a URL uses a safe protocol (http or https only).
 * Prevents javascript: and other potentially malicious URL schemes.
 */
function isValidUrl(url: string | undefined): boolean {
    if (!url) return false;
    try {
        const parsed = new URL(url);
        return ["http:", "https:"].includes(parsed.protocol);
    } catch {
        return false;
    }
}

interface PromotionItem {
    imageId?: string;
    imageUrl?: string;
    title?: string;
    description?: string;
    url?: string;
}

interface PromotionsSectionConfig {
    layout?: "2-up" | "3-up";
    promotions?: PromotionItem[];
}

// Extended type for fields not yet in generated client
type ExtendedChallenge = ChallengeInfoResponse & {
    promotionsSection?: PromotionsSectionConfig;
};

interface Props {
    challenge: ChallengeInfoResponse;
}

/**
 * Renders the promotions section on challenge pages.
 * Similar to "EXPLORE POLARIS OFF-ROAD VEHICLES" section in PolarisChallengePage.
 * Supports 2-up (side-by-side) or 3-up (grid) layout with images, titles, descriptions, and links.
 */
export default function ChallengePromotionsSection({challenge}: Props) {
    const extendedChallenge = challenge as ExtendedChallenge;
    const promotionsSection = extendedChallenge.promotionsSection;

    // Don't render if no promotions configured
    if (!promotionsSection?.promotions?.length) {
        return null;
    }

    const {layout = "2-up", promotions} = promotionsSection;

    return (
        <div className={classNames("brand-challenge_promotions", `layout-${layout}`)}>
            <div className="brand-challenge_promotions_header">
                <h3>Explore More</h3>
            </div>
            <div className={classNames("brand-challenge_promotions_grid", `layout-${layout}`)}>
                {promotions.map((promo, index) =>
                    <PromotionCard
                        key={`promo-${index}`}
                        promo={promo}
                        brandColor={challenge.brandColor}
                    />)}
            </div>
        </div>
    );
}

interface PromotionCardProps {
    promo: PromotionItem;
    brandColor?: string;
}

function PromotionCard({promo, brandColor}: PromotionCardProps) {
    const imageUrl = promo.imageUrl;
    const hasValidLink = isValidUrl(promo.url);

    const cardContent =
        <>
            {imageUrl &&
                <img
                    className="brand-challenge_promotions_card_image"
                    src={imageUrl}
                    alt={promo.title || "Promotion"}
                />
            }
            <div className="brand-challenge_promotions_card_text">
                {promo.title &&
                    <div className="brand-challenge_promotions_card_text_title">
                        {promo.title}
                    </div>
                }
                {promo.description &&
                    <div className="brand-challenge_promotions_card_text_description">
                        {promo.description}
                    </div>
                }
            </div>
            {hasValidLink &&
                <div className="brand-challenge_promotions_card_plus-icon">+</div>
            }
        </>;
    if (hasValidLink) {
        return (
            <a
                href={promo.url}
                target="_blank"
                rel="noopener noreferrer"
                className="brand-challenge_promotions_card"
                style={{"--promo-accent": brandColor} as React.CSSProperties}
            >
                {cardContent}
            </a>
        );
    }

    return (
        <div
            className="brand-challenge_promotions_card"
            style={{"--promo-accent": brandColor} as React.CSSProperties}
        >
            {cardContent}
        </div>
    );
}
